// ./src/components/features/EventPartnerPackages/EventPartnerPackageEditDrawer/EventPartnerPackageEditDrawer.tsx
//
// Offer a catalog package on this event, or change how it is offered.
//
// ⚠ TWO FIELDS HAVE NO DEFAULT, deliberately, and the drawer must not invent one. The server
// REFUSES a create that omits `isOpenForApplications` or `paymentMode`, because one decides who may
// apply and the other decides money. So on ADD both start EMPTY and the host has to choose; a
// pre-ticked "open" switch is exactly how somebody opens applications they never meant to open.
// On EDIT they are seeded from the record, because not sending a field is not the same as
// choosing false.
//
// ⚠ The package is CREATE-ONLY. An event offers each package at most once (the table carries
// UNIQUE (event_id, partner_package_id, removed_at_str)), so the picker hides packages already
// offered here, and on edit it is not offered at all.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stack, Group, Text, NumberInput, Radio, Paper, Skeleton } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { getFormErrors, formatErrors, utcToZonedString, zonedStringToUtc } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, StatusInput, CatalogCurrencyWarning, useCurrencyAffixes } from "@/components/generic";
import { PartnerPackageSelect } from "@/components/features/EventPartners";
import { eventZoneNote } from "@/components/features/Events/eventZone";
// ⚠ REUSED, not rebuilt: formats + full screen already live here, and the public site renders its
// output with the matching sanitizer (`renderPolicyHtml`). See the note at the top of that file.
import { PolicyTextEditor } from "@/components/features/Policies/PolicyTextEditor/PolicyTextEditor";
import {
  EventPartnerPackage,
  EventPartnerPackageInput,
  PartnerPackage,
  PARTNER_PAYMENT_MODE_VALUES,
  PARTNER_PAYMENT_MODE_LABELS,
  PARTNER_PAYMENT_MODE_HINTS,
} from "@/utils/api";

const KNOWN_FORM_FIELDS = [
  "partnerPackageId",
  "amount",
  "slotsTotal",
  "isOpenForApplications",
  "paymentMode",
  "publicSummary",
  "publicSummaryFormat",
  "applicationsCloseAt",
  "status",
];

interface FormState {
  partnerPackageId: string | null;
  amount: number | null;
  slotsTotal: number | null;
  /** ⚠ null means NOT YET CHOSEN, which is different from false. */
  isOpenForApplications: boolean | null;
  /** ⚠ "" means NOT YET CHOSEN. */
  paymentMode: string;
  publicSummary: string;
  /** richtext | markdown | html | plain. */
  publicSummaryFormat: string;
  /** A DAY on the EVENT's calendar ("YYYY-MM-DD") — applications close as it begins there (ROADMAP #93). */
  applicationsCloseAt: string | null;
  status: number;
}

const EMPTY_FORM: FormState = {
  partnerPackageId: null,
  amount: null,
  slotsTotal: null,
  isOpenForApplications: null,
  paymentMode: "",
  publicSummary: "",
  // ⚠ A NEW offering starts in rich text — the editor the host asked for. An EXISTING one keeps
  // whatever it was written in (every pre-2026-09-18 summary is `plain`), because re-reading old
  // plain text as rich text or Markdown would change what applicants see without anybody editing it.
  publicSummaryFormat: "richtext",
  applicationsCloseAt: null,
  status: STATUSES.ACTIVE,
};

interface EventPartnerPackageEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** The offering being edited; absent = add. */
  record?: EventPartnerPackage | null;
  /** Package ids already offered on this event — hidden from the picker. */
  offeredPackageIds: string[];
  loadingRecord?: boolean;
  operationError?: OpResult | null;
  submitting?: boolean;
  onSubmit: (params: EventPartnerPackageInput) => Promise<OpResult>;
  onSaved: (offering: EventPartnerPackage) => void;
  /**
   * The event's time zone — "Applications close" is a day on the event's calendar (ROADMAP #93). ⚠ Required, and the
   * caller mounts this drawer once the event is known: the form is seeded in this zone.
   */
  eventTimezone: string | null;
  /**
   * ADD only: the package to start with, already picked (price filled in from it, like picking it by hand) — the wizard's
   * Partners step (#62 P6(f)) opens this right after the host creates a package in the catalog. Ignored on edit.
   */
  defaultPackage?: PartnerPackage | null;
}

export const EventPartnerPackageEditDrawer: React.FC<EventPartnerPackageEditDrawerProps> = ({
  opened,
  onClose,
  eventId,
  record,
  offeredPackageIds,
  loadingRecord,
  operationError,
  submitting,
  onSubmit,
  onSaved,
  defaultPackage = null,
  eventTimezone,
}) => {
  const { t } = useTranslation(["partners", "common"]);
  // The closing day is the event's — said when this device keeps another zone.
  const zoneNote = eventZoneNote(t, eventTimezone, "day");
  // #A4.7b — the price is in the EVENT's currency, shown on the input; not editable here.
  const currencyAffixes = useCurrencyAffixes();
  const isEditMode = !!record?.id;
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);
  // The text editor is in full screen. ⚠ One Esc there would close full screen AND this drawer (Mantine's listeners sit on
  // the window and none stops it), losing what was typed — so the drawer's own Esc, click-outside and shortcuts are off
  // meanwhile (the Schedule drawer's fix, 2026-09-30).
  const [textFullscreen, setTextFullscreen] = useState(false);
  const [pickedPackage, setPickedPackage] = useState<PartnerPackage | null>(null);
  // ⚠ Seed off the record id, never off a loaded boolean — an eslint-disabled dep list will
  // otherwise re-seed the form with an empty record. See em-draft-seeding-effect.
  const seededForRef = useRef<string | null>(null);
  const snapshotRef = useRef<string>("");

  const showLoadingSkeleton = !!loadingRecord && isEditMode && !record?.id;

  useEffect(() => {
    if (!opened) {
      seededForRef.current = null;
      return;
    }

    // ⓘ A different starting package is a different new form.
    const key = record?.id || `new:${defaultPackage?.id ?? ""}`;

    if (seededForRef.current === key) {
      return;
    }

    const next: FormState = record?.id
      ? {
          partnerPackageId: record.partnerPackageId ?? null,
          amount: record.amount === null || record.amount === undefined ? null : Number(record.amount),
          slotsTotal: record.slotsTotal === null || record.slotsTotal === undefined ? null : Number(record.slotsTotal),
          isOpenForApplications: record.isOpenForApplications ?? null,
          paymentMode: record.paymentMode || "",
          publicSummary: record.publicSummary || "",
          publicSummaryFormat: record.publicSummaryFormat || "plain",
          applicationsCloseAt: utcToZonedString(record.applicationsCloseAt, eventTimezone, true),
          status: record.status ?? STATUSES.ACTIVE,
        }
      : defaultPackage?.id
        ? {
            ...EMPTY_FORM,
            partnerPackageId: defaultPackage.id,
            amount: defaultPackage.defaultAmount != null ? Number(defaultPackage.defaultAmount) : null,
          }
        : EMPTY_FORM;

    seededForRef.current = key;
    setFormData(next);
    setPickedPackage(record?.id ? null : defaultPackage);
    snapshotRef.current = JSON.stringify(next);
    setIsDirty(false);
  }, [opened, record, defaultPackage, eventTimezone]);

  useEffect(() => {
    if (!opened || showLoadingSkeleton) {
      return;
    }

    setIsDirty(JSON.stringify(formData) !== snapshotRef.current);
  }, [formData, opened, showLoadingSkeleton]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(operationError, KNOWN_FORM_FIELDS),
    [operationError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  // Picking a package prefills the price from the catalog, so the common case is one click.
  const handlePackage = useCallback((id: string | null, pkg: PartnerPackage | null) => {
    setPickedPackage(pkg);
    setFormData((prev) => ({
      ...prev,
      partnerPackageId: id,
      amount: prev.amount === null && pkg?.defaultAmount != null ? Number(pkg.defaultAmount) : prev.amount,
    }));
  }, []);

  /**
   * Switching the format keeps the text, and carries PLAIN text into rich text as paragraphs.
   *
   * ⚠ Without this, turning an old plain summary into rich text collapses it into ONE paragraph —
   * the editor reads bare text as a single run and every line break the host typed disappears. Only
   * applied when the text has no markup yet, so switching back and forth never double-wraps.
   */
  const handleSummaryFormat = useCallback((next: string) => {
    setFormData((prev) => {
      const text = prev.publicSummary;
      const toHtml = (next === "richtext" || next === "html") && prev.publicSummaryFormat === "plain";

      if (!toHtml || !text.trim() || /<[a-z][\s\S]*>/i.test(text)) {
        return { ...prev, publicSummaryFormat: next };
      }

      const escape = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const html = text
        .split(/\r?\n/)
        .map((line) => `<p>${escape(line)}</p>`)
        .join("");

      return { ...prev, publicSummaryFormat: next, publicSummary: html };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const params: EventPartnerPackageInput = {
      amount: formData.amount,
      slotsTotal: formData.slotsTotal,
      isOpenForApplications: formData.isOpenForApplications,
      paymentMode: formData.paymentMode || null,
      publicSummary: formData.publicSummary.trim() || null,
      publicSummaryFormat: formData.publicSummaryFormat,
      // ⚠ The picker gives a "YYYY-MM-DD" string (Mantine 8) — this used to call `.toISOString()` on it (a `Date` cast hid
      // it from tsc), so picking a closing day made Save throw. The day begins on the event's clock.
      applicationsCloseAt: zonedStringToUtc(formData.applicationsCloseAt, eventTimezone),
      status: formData.status,
    };

    if (!isEditMode) {
      params.eventId = eventId;
      params.partnerPackageId = formData.partnerPackageId;
    }

    const result = await onSubmit(params);

    if (result.isOk()) {
      const saved = (result.data?.[0] as EventPartnerPackage) || {};
      onSaved(saved);
    }

    return result;
  }, [formData, isEditMode, eventId, onSubmit, onSaved, eventTimezone]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !submitting && !showLoadingSkeleton,
    enabled: opened && !textFullscreen,
  });

  const packageName = record?.package?.name || pickedPackage?.name || null;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={
        isEditMode
          ? t("partners:offerings.form.editTitle", { defaultValue: "Edit offering" })
          : t("partners:offerings.actions.add", { defaultValue: "Offer a package" })
      }
      position="right"
      size="lg"
      closable={!submitting && !textFullscreen}
      loading={submitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton}
      isDirty={isDirty}
    >
      {showLoadingSkeleton ? (
        <Stack gap="md">
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={120} />
        </Stack>
      ) : (
        <Stack gap="md">
          {isEditMode ? (
            <Paper withBorder radius="sm" p="md">
              <Text size="sm" c="dimmed">
                {t("partners:offerings.columns.package", { defaultValue: "Package" })}
              </Text>
              <Text size="md" fw={600}>
                {packageName || "—"}
              </Text>
              <Text size="sm" c="dimmed">
                {t("partners:offerings.form.packageLocked", {
                  defaultValue: "An event offers each package once, so this cannot be changed. Remove the offering to swap it.",
                })}
              </Text>
            </Paper>
          ) : (
            <PartnerPackageSelect
              label={t("partners:offerings.columns.package", { defaultValue: "Package" })}
              description={t("partners:offerings.form.packageHint", {
                defaultValue: "From your account catalog. Packages already offered on this event are not listed.",
              })}
              placeholder={t("partners:offerings.form.packagePlaceholder", { defaultValue: "Choose a package" })}
              value={formData.partnerPackageId}
              onChange={handlePackage}
              eventId={eventId}
              excludeIds={offeredPackageIds}
              // A package created a moment ago (the wizard) is named at once, even before — or without — the list read.
              fallbackLabel={pickedPackage?.name ?? null}
              error={fieldErrors.partnerPackageId}
              data-autofocus
              size="md"
            />
          )}

          {/* ⚠ Starts EMPTY on add. The server refuses a create without it, and the refusal lands
              on this field — which is better than a switch that quietly defaulted to open. */}
          <Radio.Group
            label={t("partners:offerings.filters.applications", { defaultValue: "Applications" })}
            description={t("partners:offerings.form.applicationsHint", {
              defaultValue: "Whether partners can apply for this package right now.",
            })}
            value={formData.isOpenForApplications === null ? "" : formData.isOpenForApplications ? "open" : "shut"}
            onChange={(v) => setFormData((p) => ({ ...p, isOpenForApplications: v === "open" }))}
            error={fieldErrors.isOpenForApplications}
            size="md"
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="open"
                label={t("partners:offerings.form.openLabel", { defaultValue: "Open — accepting applications" })}
                size="md"
              />
              <Radio
                value="shut"
                label={t("partners:offerings.form.shutLabel", {
                  defaultValue: "Shut — listed, but not accepting applications",
                })}
                size="md"
              />
            </Stack>
          </Radio.Group>

          {/* ⚠ Also starts empty, and each option carries its consequence in words. A host should
              never have to guess what "on_approval" does to their money. */}
          <Radio.Group
            label={t("partners:offerings.form.paymentMode", { defaultValue: "How partners pay" })}
            value={formData.paymentMode}
            onChange={(paymentMode) => setFormData((p) => ({ ...p, paymentMode }))}
            error={fieldErrors.paymentMode}
            size="md"
          >
            <Stack gap="xs" mt="xs">
              {PARTNER_PAYMENT_MODE_VALUES.map((mode) => (
                <Radio
                  key={mode}
                  value={mode}
                  label={PARTNER_PAYMENT_MODE_LABELS[mode]}
                  description={PARTNER_PAYMENT_MODE_HINTS[mode]}
                  size="md"
                />
              ))}
            </Stack>
          </Radio.Group>

          <Group grow align="flex-start">
            <NumberInput
              label={t("partners:offerings.form.price", { defaultValue: "Price for this event" })}
              description={t("partners:offerings.form.priceHint", { defaultValue: "Leave empty to use the catalog price." })}
              value={formData.amount ?? ""}
              onChange={(v) => setFormData((p) => ({ ...p, amount: v === "" || v === null ? null : Number(v) }))}
              min={0}
              decimalScale={2}
              error={fieldErrors.amount}
              size="md"
              {...currencyAffixes}
            />
          </Group>

          <CatalogCurrencyWarning sourceCurrencyCode={pickedPackage?.currencyCode} sourceLabel={t("partners:offerings.form.currencySource", { defaultValue: "This package" })} />

          <NumberInput
            label={t("partners:offerings.columns.slots", { defaultValue: "Slots" })}
            description={t("partners:offerings.form.slotsHint", {
              defaultValue: "How many partners can take this package. Leave empty for unlimited.",
            })}
            value={formData.slotsTotal ?? ""}
            onChange={(v) => setFormData((p) => ({ ...p, slotsTotal: v === "" || v === null ? null : Number(v) }))}
            min={0}
            error={fieldErrors.slotsTotal}
            size="md"
          />

          <DatePickerInput
            label={t("partners:offerings.form.closeAt", { defaultValue: "Applications close" })}
            description={[
              t("partners:offerings.form.closeAtHint", {
                defaultValue: "Leave empty to keep them open until the event.",
              }),
              zoneNote,
            ]
              .filter(Boolean)
              .join(" ")}
            value={formData.applicationsCloseAt}
            onChange={(v) => setFormData((p) => ({ ...p, applicationsCloseAt: v ? String(v) : null }))}
            error={fieldErrors.applicationsCloseAt}
            size="md"
            clearable
          />

          <PolicyTextEditor
            label={t("partners:offerings.form.publicSummary", { defaultValue: "What applicants see" })}
            description={t("partners:offerings.form.publicSummaryHint", {
              defaultValue: "Shown on the application form. Your catalog description stays private.",
            })}
            format={formData.publicSummaryFormat}
            onFormatChange={handleSummaryFormat}
            value={formData.publicSummary}
            // A VALUE, never an event — see check-deferred-event-read.
            onChange={(value) => setFormData((p) => ({ ...p, publicSummary: value }))}
            onFullscreenChange={setTextFullscreen}
            error={fieldErrors.publicSummary || fieldErrors.publicSummaryFormat}
            // No merge variables: nothing on the public site would fill them in.
            variables={[]}
          />

          <StatusInput
            label={t("partners:offerings.filters.listed", { defaultValue: "Listed" })}
            description={t("partners:offerings.form.listedHint", {
              defaultValue: "Draft keeps the offering off the application page entirely.",
            })}
            value={formData.status}
            onChange={(status) => setFormData((p) => ({ ...p, status }))}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
